import { useMemo, useState } from 'react';
import { Card, Title, Text, Flex } from '@tremor/react';
import { Link } from 'react-router-dom';
import { RangeSelectChart } from '../components/RangeSelectChart';
import {
  useApiUsageSummary,
  useApiUsageTimeseries,
  useApiUsageByEndpoint,
  useApiUsageByApiKey,
  useApiUsageByOrg,
} from '../hooks/useApiUsageStats';

const PRESETS: Array<{ id: '1h' | '24h' | '7d' | '30d'; label: string; ms: number }> = [
  { id: '1h', label: 'Last hour', ms: 60 * 60_000 },
  { id: '24h', label: 'Last 24h', ms: 24 * 60 * 60_000 },
  { id: '7d', label: 'Last 7d', ms: 7 * 24 * 60 * 60_000 },
  { id: '30d', label: 'Last 30d', ms: 30 * 24 * 60 * 60_000 },
];

function formatBucket(s: string): string {
  // ISO datetime → short localized label. Hour buckets keep HH:mm; day buckets
  // drop the time for legibility.
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function pctFormatter(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function msFormatter(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(2)}s` : `${Math.round(n)}ms`;
}

function num(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}

export function MonitoringUsagePage() {
  const [preset, setPreset] = useState<typeof PRESETS[number]['id']>('24h');
  const [orgId, setOrgId] = useState('');
  const [apiKeyId, setApiKeyId] = useState('');
  const [path, setPath] = useState('');

  // Memoize the params object so it's a stable reference for the SWR keys —
  // otherwise each render re-keys all the hooks and refetches.
  const params = useMemo(() => {
    const ms = PRESETS.find((p) => p.id === preset)?.ms ?? PRESETS[1].ms;
    const to = new Date();
    const from = new Date(to.getTime() - ms);
    return {
      from: from.toISOString(),
      to: to.toISOString(),
      organizationId: orgId.trim() || undefined,
      apiKeyId: apiKeyId.trim() || undefined,
      path: path.trim() || undefined,
    };
  }, [preset, orgId, apiKeyId, path]);

  const summary = useApiUsageSummary(params);
  const timeseries = useApiUsageTimeseries(params);
  const endpoints = useApiUsageByEndpoint(params);
  const byApiKey = useApiUsageByApiKey(params);
  const byOrg = useApiUsageByOrg(params);

  const timeseriesRows = useMemo(() => {
    return (timeseries.data?.buckets || []).map((b) => ({
      bucket: formatBucket(b.bucket),
      totalRequests: b.totalRequests,
      errorRequests: b.errorRequests,
    }));
  }, [timeseries.data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold text-gray-100">API Usage</h1>
        <div className="inline-flex rounded border border-gray-800 bg-gray-900 p-0.5 gap-0.5">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPreset(p.id)}
              className={`px-3 py-1.5 text-sm rounded ${preset === p.id ? 'bg-blue-900/60 text-blue-100' : 'text-gray-400 hover:text-gray-200'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-900 border border-gray-800 rounded p-3 flex flex-wrap items-end gap-3">
        <Field label="Organization ID or slug">
          <input
            type="text"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            placeholder="filter by org…"
            className="w-72 bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100"
          />
        </Field>
        <Field label="API key id">
          <input
            type="text"
            value={apiKeyId}
            onChange={(e) => setApiKeyId(e.target.value)}
            placeholder="ObjectId…"
            className="w-72 bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100"
          />
        </Field>
        <Field label="Endpoint">
          <input
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/v1/reflections"
            className="w-56 bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100"
          />
        </Field>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard label="Requests" value={num(summary.data?.totalRequests)} />
        <SummaryCard
          label="Errors"
          value={num(summary.data?.errorRequests)}
          subtitle={`${pctFormatter(summary.data?.errorRate)} error rate`}
        />
        <SummaryCard label="Avg latency" value={msFormatter(summary.data?.avgDurationMs)} />
        <SummaryCard
          label="Latency"
          value={msFormatter(summary.data?.durationMs?.p95)}
          subtitle={`p50 ${msFormatter(summary.data?.durationMs?.p50)} · p99 ${msFormatter(summary.data?.durationMs?.p99)}`}
        />
      </div>

      <Card className="bg-gray-900 ring-gray-800">
        <Flex justifyContent="between" alignItems="baseline">
          <Title className="text-gray-100">Requests over time</Title>
          <Text className="text-gray-500 text-xs">
            granularity: {timeseries.data?.granularity ?? 'auto'}
          </Text>
        </Flex>
        <div className="mt-3">
          <RangeSelectChart
            data={timeseriesRows}
            xKey="bucket"
            heightClass="h-64"
            series={[
              { key: 'totalRequests', label: 'Requests', color: 'cyan' },
              { key: 'errorRequests', label: 'Errors', color: 'rose' },
            ]}
          />
        </div>
      </Card>

      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">By endpoint</Title>
        <table className="w-full text-sm mt-3">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-2 py-1 font-medium">Method</th>
              <th className="px-2 py-1 font-medium">Path</th>
              <th className="px-2 py-1 font-medium text-right">Requests</th>
              <th className="px-2 py-1 font-medium text-right">Errors</th>
              <th className="px-2 py-1 font-medium text-right">Error rate</th>
              <th className="px-2 py-1 font-medium text-right">Avg ms</th>
            </tr>
          </thead>
          <tbody>
            {(endpoints.data?.endpoints || []).map((row) => (
              <tr key={`${row.method} ${row.path}`} className="border-b border-gray-800/50">
                <td className="px-2 py-1.5 text-gray-300">{row.method}</td>
                <td className="px-2 py-1.5"><code className="text-xs text-gray-200">{row.path}</code></td>
                <td className="px-2 py-1.5 text-right text-gray-100">{num(row.totalRequests)}</td>
                <td className="px-2 py-1.5 text-right text-gray-400">{num(row.errorRequests)}</td>
                <td className="px-2 py-1.5 text-right text-gray-400">{pctFormatter(row.errorRate)}</td>
                <td className="px-2 py-1.5 text-right text-gray-400">{msFormatter(row.avgDurationMs)}</td>
              </tr>
            ))}
            {endpoints.data && endpoints.data.endpoints.length === 0 && (
              <tr><td colSpan={6} className="px-2 py-4 text-center text-gray-500 text-xs">No requests in this window</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">By API key</Title>
        <Text className="text-gray-500 text-xs mt-1">Top 50 by volume.</Text>
        <table className="w-full text-sm mt-3">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-2 py-1 font-medium">Key</th>
              <th className="px-2 py-1 font-medium">Owner</th>
              <th className="px-2 py-1 font-medium">Org</th>
              <th className="px-2 py-1 font-medium">Env</th>
              <th className="px-2 py-1 font-medium text-right">Requests</th>
              <th className="px-2 py-1 font-medium text-right">Error rate</th>
              <th className="px-2 py-1 font-medium">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {(byApiKey.data?.apiKeys || []).map((row) => (
              <tr key={row.apiKeyId} className="border-b border-gray-800/50">
                <td className="px-2 py-1.5">
                  <div className="text-gray-200">{row.name ?? <span className="text-gray-500">unknown</span>}</div>
                  <code className="text-xs text-gray-500">{row.prefix ?? row.apiKeyId}</code>
                </td>
                <td className="px-2 py-1.5 text-xs text-gray-400">{row.user?.email ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">
                  {row.organization ? (
                    <Link to={`/admin/orgs/${row.organization._id}`} className="text-blue-400 hover:underline">
                      {row.organization.name ?? row.organization._id}
                    </Link>
                  ) : (
                    <span className="text-gray-600">personal</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-xs text-gray-400">{row.environment ?? '—'}</td>
                <td className="px-2 py-1.5 text-right text-gray-100">{num(row.totalRequests)}</td>
                <td className="px-2 py-1.5 text-right text-gray-400">{pctFormatter(row.errorRate)}</td>
                <td className="px-2 py-1.5 text-xs text-gray-500">{new Date(row.lastRequestAt).toLocaleString()}</td>
              </tr>
            ))}
            {byApiKey.data && byApiKey.data.apiKeys.length === 0 && (
              <tr><td colSpan={7} className="px-2 py-4 text-center text-gray-500 text-xs">No keyed traffic in this window</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">By organization</Title>
        <table className="w-full text-sm mt-3">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-2 py-1 font-medium">Organization</th>
              <th className="px-2 py-1 font-medium text-right">Requests</th>
              <th className="px-2 py-1 font-medium text-right">Error rate</th>
            </tr>
          </thead>
          <tbody>
            {(byOrg.data?.organizations || []).map((row, idx) => (
              <tr key={row.organization?._id ?? `personal-${idx}`} className="border-b border-gray-800/50">
                <td className="px-2 py-1.5 text-sm">
                  {row.organization ? (
                    <Link
                      to={`/admin/orgs/${row.organization._id}`}
                      className="text-blue-400 hover:underline"
                    >
                      {row.organization.name ?? row.organization._id}
                    </Link>
                  ) : (
                    <span className="text-gray-400">Personal (no org)</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right text-gray-100">{num(row.totalRequests)}</td>
                <td className="px-2 py-1.5 text-right text-gray-400">{pctFormatter(row.errorRate)}</td>
              </tr>
            ))}
            {byOrg.data && byOrg.data.organizations.length === 0 && (
              <tr><td colSpan={3} className="px-2 py-4 text-center text-gray-500 text-xs">No requests</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      {children}
    </label>
  );
}

function SummaryCard({ label, value, subtitle }: { label: string; value: string; subtitle?: string }) {
  return (
    <Card className="bg-gray-900 ring-gray-800">
      <Text className="text-gray-400 text-xs uppercase tracking-wide">{label}</Text>
      <div className="text-2xl text-gray-100 font-semibold mt-1">{value}</div>
      {subtitle && <Text className="text-gray-500 text-xs mt-1">{subtitle}</Text>}
    </Card>
  );
}
